import styled from "@emotion/styled";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import {
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { addTokenToStore, handleGetChartData } from "utils/tokenSelectorUtils";
import { TokenSelectorDropdown } from "./TokenSelectorDropdown";
import { AnimatePresence } from "framer-motion";
import { CounterParty } from "utils/tradeUtils";
import { Hash } from "viem";
import { TokenSelectorInput } from "./elements/TokenSelectorInput";
import { AutoCompleteSuggestion } from "./elements/AutoCompleteSuggestion";
import CloseIcon from "../../img/butter_svgs/Close.svg?react";
import { useTokenlistMetadataByChain } from "../../hooks/queries/tokens-queries";
import { sortTokensByBusinessLogic } from "../../utils/TokenSelector/utils";

interface TokenSelectorProps {
  counterParty: CounterParty;
  isChangingToken: Hash | undefined;
  isDropdownDisplayed: boolean;
  setIsDropdownDisplayed: React.Dispatch<SetStateAction<boolean>>;
}

export const TokenSelector = forwardRef<HTMLDivElement, TokenSelectorProps>(
  ({ counterParty, isChangingToken, isDropdownDisplayed, setIsDropdownDisplayed }, ref) => {
    const [searchString, setSearchString] = useState("");
    const [highlightedRowIdx, setHighlightedRowIdx] = useState<number | null>(null);
    const { balances, makerTokens, takerTokens, chain, chartLists } = GlobalStore.useState(s => ({
      balances: s.balances,
      makerTokens: s.makerTokens.map(e => e.tokenAddress),
      takerTokens: s.takerTokens.map(e => e.tokenAddress),
      chain: s.network,
      chartLists: s.chartLists,
    }));

    const currentPartyTokens = counterParty === "taker" ? takerTokens : makerTokens;

    const tokenList = useTokenListByChain();
    const { data: tokenListMetadata } = useTokenlistMetadataByChain({ chainId: chain.id });

    const inputRef = useRef<HTMLInputElement | null>(null);

    const tokensToDisplay = useMemo(
      () =>
        sortTokensByBusinessLogic(
          makerTokens,
          takerTokens,
          tokenList,
          tokenListMetadata,
          balances,
          searchString,
          chain
        ),
      [makerTokens, takerTokens, searchString, balances, tokenList, tokenListMetadata, chain]
    );

    const handleKeyPress = useCallback(
      (e: KeyboardEvent) => {
        if (tokensToDisplay == null) return;
        switch (e.key) {
          case "ArrowDown":
            if (highlightedRowIdx == null) {
              setHighlightedRowIdx(0);
            } else {
              const nextIdx =
                highlightedRowIdx === tokensToDisplay.length - 1 ? 0 : highlightedRowIdx + 1;
              setHighlightedRowIdx(nextIdx);
              handleGetChartData(tokensToDisplay[nextIdx], chain, chartLists);
            }
            break;
          case "ArrowUp":
            if (highlightedRowIdx == null) {
              setHighlightedRowIdx(tokensToDisplay.length - 1);
            } else {
              const nextIdx =
                highlightedRowIdx === 0 ? tokensToDisplay.length - 1 : highlightedRowIdx - 1;
              setHighlightedRowIdx(nextIdx);
            }
            break;
          case "Enter": {
            if (highlightedRowIdx == null) return;
            const selectedToken = tokensToDisplay[highlightedRowIdx];
            if (selectedToken == null) return;
            addTokenToStore(counterParty, selectedToken);
            break;
          }
          case "Escape":
            setIsDropdownDisplayed(false);
            break;
          default:
            break;
        }
      },
      [counterParty, highlightedRowIdx, tokensToDisplay, setIsDropdownDisplayed, chartLists, chain]
    );

    useEffect(() => {
      if (isDropdownDisplayed) {
        window.addEventListener("keydown", handleKeyPress);
      } else {
        window.removeEventListener("keydown", handleKeyPress);
      }
      return () => {
        window.removeEventListener("keydown", handleKeyPress);
      };
    }, [handleKeyPress, isDropdownDisplayed]);

    useEffect(() => {
      if (currentPartyTokens.length === 0) return;
      inputRef.current?.focus();
      setIsDropdownDisplayed(true);
    }, [currentPartyTokens.length, setIsDropdownDisplayed]);

    // Remove dropdown row highlights if search value changes
    useEffect(() => {
      if (searchString === "") return;
      setHighlightedRowIdx(0);
    }, [searchString]);

    useEffect(() => {
      setSearchString("");
    }, [chain]);

    const closeDropdown = useCallback(() => {
      setIsDropdownDisplayed(false);
    }, [setIsDropdownDisplayed]);

    const scrollToTop = useCallback(() => {
      console.log(`noop`);
    }, []);

    return (
      <AnimatePresence>
        <Container ref={ref}>
          <InputContainer>
            <TokenSelectorInput
              isDropdownDisplayed={isDropdownDisplayed}
              counterParty={counterParty}
              searchString={searchString}
              currentPartyTokens={currentPartyTokens}
              setSearchString={setSearchString}
              setIsDropdownDisplayed={setIsDropdownDisplayed}
              ref={inputRef}
            />
            {tokensToDisplay && (
              <AutoCompleteSuggestion
                tokenList={tokenList}
                searchString={searchString}
                tokensSortedByBalance={tokensToDisplay}
              />
            )}
            {searchString && (
              <ClearButton
                onClick={() => {
                  setSearchString("");
                  inputRef.current?.focus();
                }}
              >
                Clear
              </ClearButton>
            )}
            {isDropdownDisplayed && !searchString && (
              <CloseButton onClick={() => setIsDropdownDisplayed(false)}>
                <CloseIcon width={20} height={20} />
              </CloseButton>
            )}
          </InputContainer>
          <TokenSelectorDropdown
            isHidden={!isDropdownDisplayed}
            setHighlightedRowIdx={setHighlightedRowIdx}
            counterParty={counterParty}
            searchString={searchString}
            tokensToShow={tokensToDisplay}
            highlightedRowIdx={highlightedRowIdx}
            closeDropdown={closeDropdown}
            isChangingToken={isChangingToken}
            scrollToTop={scrollToTop}
            tokenList={tokenList}
            tokenListMetadata={tokenListMetadata}
          />
        </Container>
      </AnimatePresence>
    );
  }
);

// Sub-components

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const InputContainer = styled.div`
  padding: 15px 26px 14px 26px;
  position: relative;
  width: 100%;
  height: 100%;
`;

const ClearButton = styled.button`
  position: absolute;
  top: 18px;
  right: 22px;
  color: ${({ theme }) => theme.font.tertiary.default};
  background: none;
  border: none;
  transition: color linear 0.2s;
  z-index: 20;

  &:hover {
    color: ${({ theme }) => theme.font.tertiary.hover};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 22px;
  color: ${({ theme }) => theme.font.secondary.default};
  background: ${({ theme }) => theme.button.tertiary.surface.default};
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    color linear 0.2s,
    background linear 0.2s;
  z-index: 20;

  &:hover {
    color: ${({ theme }) => theme.font.tertiary.hover};
    background: ${({ theme }) => theme.button.tertiary.surface.hover};
  }
`;
